import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModalBottom } from '../../../actions/ui';
import { startSaveProfesional } from '../../../actions/profesionales';
import { AddPermisoSelect } from './permisos/AddPermisoSelect';
import { AddSeccionesSelect } from './permisos/AddSeccionesSelect';
import { fetchConToken } from '../../../helpers/fetch';

const secciones = [
    { key: 'reservas', text: 'Reservas' },
    { key: 'profesionales', text: 'Profesionales' },
    { key: 'clientes', text: 'Clientes' },
    { key: 'punto', text: 'Punto de venta' },
    { key: 'precios', text: 'Precios especiales' },
    { key: 'notificaciones', text: 'Notificaciones' },
    { key: 'locales', text: 'Locales' },
    { key: 'configuracion', text: 'Configuración' },
    { key: 'admin', text: 'Admin' },
];

export const Permisos = ({ objProfesional, setObjProfesional, editMode }) => {

    const dispatch = useDispatch();
    const { colores } = useSelector( state => state.ui );
    const { empresa } = useSelector( state => state.auth );
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [hasUser, setHasUser] = useState(false);
    const [loading, setLoading] = useState(true);
    const [creatingUser, setCreatingUser] = useState(false);

    useEffect(() => {
        // Check if professional has an associated user
        const checkUserAssociation = async () => {
            if (objProfesional?._id) {
                try {
                    setLoading(true);
                    
                    if (objProfesional.usuario) {
                        // Si el profesional ya tiene un usuario asociado, obtener información del usuario
                        const resp = await fetchConToken(`usuarios/usuario`, 
                            { usuario: objProfesional.usuario, empresa }, 
                            'POST'
                        );
                        const body = await resp.json();
                        
                        if (body.ok && body.usuario) {
                            setHasUser(true);
                            setEmail(body.usuario.email || '');
                        } else {
                            setHasUser(false);
                            setEmail('');
                        }
                    } else {
                        // El profesional no tiene usuario asociado
                        setHasUser(false);
                        setEmail('');
                    }
                } catch (error) {
                    console.error('Error checking user association:', error);
                    setHasUser(false);
                } finally {
                    setLoading(false);
                }
            }
        };

        checkUserAssociation();
    }, [objProfesional?._id, objProfesional?.usuario, empresa]);

    const updatePermisos = ( permiso ) => {
        if( editMode ){
            let newPermisos = [];
    
            if( !objProfesional.permisos.includes( permiso.key ) ) {
                newPermisos = [permiso.key];
            }
            const newProfesional = { ...objProfesional };
            newProfesional.permisos = newPermisos;
            setObjProfesional( newProfesional );
        } else {
            dispatch( openModalBottom({text: ['Debes activar la edición para poder realizar cambios'], timeOut: 3}) );
        }
    }
    
    const updateSecciones = ( seccion ) => {
        if( editMode ){
            let newSecciones = [];

            if( seccion.key === 'todas' ){
                if( !checkAllSeccionesSelected() ){
                    secciones.forEach(item => {
                        newSecciones = [ ...newSecciones, item.key ];
                    });
                }
            }
    
            if( seccion.key !== 'todas' ){
                if( !objProfesional.secciones.includes( seccion.key ) ) {
                    newSecciones = [ ...objProfesional.secciones, seccion.key];
                } else {
                    newSecciones = objProfesional.secciones.filter( item => item !== seccion.key );
                }
            }

            const newProfesional = { ...objProfesional };
            newProfesional.secciones = newSecciones;
            setObjProfesional( newProfesional );
        } else {
            dispatch( openModalBottom({text: ['Debes activar la edición para poder realizar cambios'], timeOut: 3}) );
        }
    }

    const checkAllSeccionesSelected = () => {
        let allSelected = true;
        secciones.forEach(seccion => {
            if( seccion.key !== 'todas' ){
                if(!objProfesional.secciones?.includes(seccion.key)){ allSelected = false; }
            }
        });
        return allSelected;
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    }

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    }

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    }

    const createUserForProfessional = async () => {
        if (!email || !email.includes('@')) {
            dispatch(openModalBottom({text: ['Por favor introduce un email válido'], timeOut: 3}));
            return;
        }

        if (!password || password.length < 6) {
            dispatch(openModalBottom({text: ['La contraseña debe tener al menos 6 caracteres'], timeOut: 3}));
            return;
        }

        try {
            // Preparar datos según el formato requerido

			setCreatingUser(true);

            const userData = {
                profesionalId: objProfesional._id,
                email: email,
                password: password,
                empresa: empresa // Usar el ID de empresa del state de Redux
            };

            const resp = await fetchConToken(
                'usuarios/from-profesional', 
                userData, 
                'POST'
            );
            const body = await resp.json();
			console.log(body);
            if (body.ok) {
                // Actualizar el profesional con el ID del usuario creado
                const updatedProfesional = {
                    ...objProfesional,
                    usuario: body.usuario._id
                };

                dispatch(startSaveProfesional(updatedProfesional));


                // Actualizar el estado del profesional
                setObjProfesional(updatedProfesional);
			                
                dispatch(openModalBottom({
                    text: [
                        'Usuario creado correctamente',
                        'Se ha enviado un email con instrucciones para activar la cuenta'
                    ], 
                    timeOut: 5
                }));
                
                setHasUser(true);
                // Limpiar el campo de contraseña
                setPassword('');
            } else {
                let errorMessage = 'Error al crear usuario';
                
                if (body.code === 'email_business_user') {
                    errorMessage = 'No se puede vincular este email al profesional porque ya está siendo usado por un usuario de la empresa';
                } else if (body.code === 'exists') {
                    errorMessage = 'Ya existe un usuario con el correo indicado en esta empresa';
                } else if (body.msg) {
                    errorMessage = body.msg;
                }
                
                dispatch(openModalBottom({
                    text: [errorMessage], 
                    timeOut: 5
                }));
            }
			
        } catch (error) {
            console.error('Error creating user:', error);
            dispatch(openModalBottom({text: ['Error al conectar con el servidor'], timeOut: 3}));
        } finally {
            setCreatingUser(false);
        }
    }

    const changePasswordForProfessional = async () => {
        if (!password || password.length < 6) {
            dispatch(openModalBottom({
                text: ['La contraseña debe tener al menos 6 caracteres'], 
                timeOut: 3
            }));
            return;
        }

        if (!objProfesional.usuario) {
            dispatch(openModalBottom({
                text: ['Este profesional no tiene un usuario vinculado'], 
                timeOut: 3
            }));
            return;
        }

        try {
            setCreatingUser(true);
            
            const resp = await fetchConToken(
                'usuarios/change-password',
                { password, empresa, usuarioId: objProfesional.usuario }, 
                'POST'
            );
            
            const body = await resp.json();
            
            if (body.ok) {
                dispatch(openModalBottom({
                    text: ['Contraseña actualizada correctamente'], 
                    timeOut: 3
                }));
                setPassword('');
            } else {
                dispatch(openModalBottom({
                    text: [body.msg || 'Error al cambiar la contraseña'], 
                    timeOut: 5
                }));
            }
        } catch (error) {
            console.error('Error al cambiar la contraseña:', error);
            dispatch(openModalBottom({
                text: [`Error al cambiar la contraseña: ${error.message}`], 
                timeOut: 5
            }));
        } finally {
            setCreatingUser(false);
        }
    }

    const deleteUserForProfessional = async () => {
        if (!objProfesional.usuario) return;

        try {
            setCreatingUser(true);
            
            const resp = await fetchConToken(
                `profesionales/${objProfesional._id}/usuario`, 
                { empresa }, 
                'DELETE'
            );
            const body = await resp.json();
            
            if (body.ok) {
                const updatedProfesional = {
                    ...objProfesional,
                    usuario: null
                };
                
                dispatch(startSaveProfesional(updatedProfesional));
                setObjProfesional(updatedProfesional);
                
                dispatch(openModalBottom({
                    text: ['Usuario eliminado correctamente'], 
                    timeOut: 3
                }));
                
                setHasUser(false);
                setEmail('');
                setPassword('');
            } else {
                throw new Error(body.msg || 'Error al eliminar el usuario');
            }
        } catch (error) {
            console.error('Error al eliminar el usuario:', error);
            dispatch(openModalBottom({
                text: ['Error al conectar con el servidor'], 
                timeOut: 3
            }));
        } finally {
            setCreatingUser(false);
        }
    }

    if (loading) {
        return <div className="permisos">Cargando...</div>;
    }

    const inputStyle = {
        flex: 1, 
        padding: '8px', 
        borderRadius: '4px',
        border: `1px solid ${colores.colorPrincipal}`
    };

    const buttonStyle = {
        padding: '8px 12px', 
        backgroundColor: colores.colorPrincipal,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    };

    return (
        <div className='permisos'>
            <div
                className='add-permiso'
                style={{ backgroundColor: colores.colorSecundario }}
            >
                <div className="user-container" style={{ 
                    marginBottom: '20px',
                    marginTop: '15px',
                    border: `1px solid ${colores.colorPrincipal}`,
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#fff',
                    width: '100%',
                    maxWidth: '450px',
                    margin: '15px 0 20px 0'
                }}>
                    {!hasUser ? (
                        <>
                            <h3 style={{ 
                                marginBottom: '20px', 
                                color: colores.colorPrincipal,
                                textAlign: 'center',
                                borderBottom: `1px solid ${colores.colorSecundario}`,
                                paddingBottom: '10px'
                            }}>
                                Crear cuenta de usuario
                            </h3>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email:</label>
                                    <input 
                                        type="email" 
                                        value={email} 
                                        onChange={handleEmailChange}
                                        placeholder="Introduce email para crear usuario" 
                                        disabled={!editMode}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contraseña:</label>
                                    <div className="password-container">
                                        <input 
                                            type={showPassword ? "text" : "password"} 
                                            value={password} 
                                            onChange={handlePasswordChange}
                                            placeholder="Introduce contraseña (min. 6 caracteres)" 
                                            disabled={!editMode}
                                        />
                                        <button
                                            type="button"
                                            onClick={toggleShowPassword}
                                        >
                                            {showPassword ? "Ocultar" : "Mostrar"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {editMode && (
                                <div className="button-container">
                                    <button 
                                        onClick={createUserForProfessional}
                                        disabled={creatingUser}
                                        className="primary"
                                    >
                                        {creatingUser ? (
                                            <>
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    border: '2px solid #ffffff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }} />
                                                Procesando...
                                            </>
                                        ) : (
                                            'Crear Usuario'
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '20px'
                        }}>
                            <h3 style={{ 
                                marginBottom: '20px', 
                                color: colores.colorPrincipal,
                                textAlign: 'center',
                                borderBottom: `1px solid ${colores.colorSecundario}`,
                                paddingBottom: '10px'
                            }}>
                                Usuario vinculado
                            </h3>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email de acceso:</label>
                                    <input 
                                        type="email" 
                                        value={email} 
                                        disabled={true}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Cambiar contraseña:</label>
                                    <div className="password-container">
                                        <input 
                                            type={showPassword ? "text" : "password"} 
                                            value={password} 
                                            onChange={handlePasswordChange}
                                            placeholder="Introduce nueva contraseña (min. 6 caracteres)" 
                                            disabled={!editMode}
                                        />
                                        <button
                                            type="button"
                                            onClick={toggleShowPassword}
                                        >
                                            {showPassword ? "Ocultar" : "Mostrar"}
                                        </button>
                                    </div>
                                    {editMode && (
                                        <div className="button-container">
                                            <button 
                                                onClick={changePasswordForProfessional}
                                                disabled={creatingUser}
                                                className="primary"
                                            >
                                                {creatingUser ? (
                                                    <>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '2px solid #ffffff',
                                                            borderTop: '2px solid transparent',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }} />
                                                        Procesando...
                                                    </>
                                                ) : (
                                                    'Cambiar Contraseña'
                                                )}
                                            </button>
                                            <button 
                                                onClick={deleteUserForProfessional}
                                                disabled={creatingUser}
                                                className="danger"
                                            >
                                                {creatingUser ? (
                                                    <>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '2px solid #ffffff',
                                                            borderTop: '2px solid transparent',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }} />
                                                        Procesando...
                                                    </>
                                                ) : (
                                                    'Eliminar Usuario'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Show permissions only if user exists */}
                {/* TODO: Implement permission selectors
                {hasUser && (
                    <div className="permisos-selectors">
                        <AddPermisoSelect
                            objProfesional={objProfesional}
                            updatePermisos={updatePermisos}
                        />
                        <AddSeccionesSelect
                            secciones={ secciones }
                            objProfesional={objProfesional}
                            updateSecciones={updateSecciones}
                            checkAllSeccionesSelected={ checkAllSeccionesSelected }
                        />
                    </div>
                )}
                */}
            </div>
        </div>
    )
}
